import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, UserRound } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import {
  circulationApi,
  libraryFinesApi,
  libraryMembersApi,
  reservationsApi,
} from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import { formatDate, orDash } from '@/utils/format';
import {
  Blockers,
  Field,
  FineStatusBadge,
  LoanStatusBadge,
  MemberStatusBadge,
  Money,
  ReservationStatusBadge,
  Stat,
  humanise,
  overdueText,
} from './shared';

/**
 * One member: what they have out, what they are waiting for, what they owe.
 *
 * <p>The four panels are the four questions a librarian is actually asked at the
 * desk, in the order they get asked. The eligibility panel is first because it
 * is the one that stops the conversation.
 */
export function LibraryMemberDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const seesFines = can(PERMISSIONS.LIBRARY_FINE_VIEW);

  const member = useQuery({
    queryKey: queryKeys.libraryMembers.detail(id),
    queryFn: () => libraryMembersApi.get(id),
  });

  const eligibility = useQuery({
    queryKey: queryKeys.libraryMembers.eligibility(id),
    queryFn: () => libraryMembersApi.eligibility(id),
  });

  const loans = useQuery({
    queryKey: queryKeys.circulation.loans({ memberId: id, size: 50, sort: 'issueDate,desc' }),
    queryFn: () => circulationApi.loans({ memberId: id, size: 50, sort: 'issueDate,desc' }),
  });

  const reservations = useQuery({
    queryKey: queryKeys.reservations.list({ memberId: id }),
    queryFn: () => reservationsApi.list({ size: 50 }),
  });

  const fines = useQuery({
    queryKey: queryKeys.libraryFines.forMember(id),
    queryFn: () => libraryFinesApi.forMember(id),
    enabled: seesFines,
  });

  if (member.isError) {
    return <ErrorState error={member.error} onRetry={() => void member.refetch()} />;
  }
  if (member.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = member.data;
  const theirReservations = (reservations.data?.data ?? []).filter((row) => row.memberId === id);

  return (
    <div>
      <Link
        to={ROUTES.libraryMembers}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        All members
      </Link>

      <PageHeader
        title={data.name}
        description={`${data.membershipNumber} · ${humanise(data.memberType)} · ${data.libraryName}`}
        icon={<UserRound className="h-5 w-5" aria-hidden="true" />}
        action={<MemberStatusBadge status={data.status} />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Out now"
          value={`${data.booksOut} of ${data.borrowingLimit}`}
          hint={data.limitIsOverridden ? 'Limit raised for them' : 'The limit for their type'}
        />
        <Stat label="Loan period" value={`${data.loanDays} days`} hint={`${data.maxRenewals} renewals`} />
        {seesFines && (
          <Stat
            label="Owed"
            value={<Money value={data.outstandingFines} />}
            tone={data.outstandingFines > 0 ? 'warning' : 'neutral'}
          />
        )}
        <Stat
          label="Waiting for"
          value={theirReservations.filter((row) => row.status === 'PENDING' || row.status === 'READY').length}
        />
      </div>

      {eligibility.data !== undefined && !eligibility.data.canBorrow && (
        <div className="mt-3">
          <Blockers reasons={eligibility.data.blockers} title="Cannot borrow" />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Borrowing history
          </h2>
          {loans.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (loans.data?.data ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">Nothing borrowed yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {(loans.data?.data ?? []).map((loan) => (
                <li key={loan.id} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{loan.bookTitle}</span>
                    <span className="block text-[11px] text-subtle">
                      {formatDate(loan.issueDate)} → {orDash(
                        loan.returnDate === undefined ? undefined : formatDate(loan.returnDate),
                      )}
                      {loan.overdue && ` · ${overdueText(loan.daysOverdue)}`}
                    </span>
                  </span>
                  <LoanStatusBadge status={loan.status} overdue={loan.overdue} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Reservations
            </h2>
            {theirReservations.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">Not waiting for anything.</p>
            ) : (
              <ul className="mt-2 divide-y divide-line">
                {theirReservations.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{row.bookTitle}</span>
                      <span className="block text-[11px] text-subtle">
                        {row.status === 'PENDING'
                          ? `number ${row.queuePosition} in the queue`
                          : row.status === 'READY'
                            ? `collect by ${row.collectBy === undefined ? '—' : formatDate(row.collectBy)}`
                            : formatDate(row.requestedAt)}
                      </span>
                    </span>
                    <ReservationStatusBadge status={row.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {seesFines && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Fines</h2>
              {(fines.data ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">Never been fined.</p>
              ) : (
                <ul className="mt-2 divide-y divide-line">
                  {(fines.data ?? []).map((fine) => (
                    <li key={fine.id} className="flex flex-wrap items-center gap-3 py-2">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-ink">{fine.reason}</span>
                        <span className="block text-[11px] text-subtle">
                          {fine.fineNumber} · {formatDate(fine.chargedOn)}
                        </span>
                      </span>
                      <Money value={fine.outstanding} />
                      <FineStatusBadge status={fine.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Membership
            </h2>
            <dl className="mt-2">
              <Field label="Joined">{formatDate(data.registeredOn)}</Field>
              <Field label="Expires">
                {data.expiresOn === undefined ? 'Does not expire' : formatDate(data.expiresOn)}
              </Field>
              <Field label="Library">{data.libraryName}</Field>
              {data.notes !== undefined && <Field label="Notes">{data.notes}</Field>}
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}
